/**
 * Growth — RFM segmentation. Stage 6.
 * Sources `/api/bi/growth/rfm/`.
 */
import { useMemo, useState } from 'react';
import { type ColumnDef } from '@tanstack/react-table';
import { Sparkles, Users } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Badge } from '@/components/ui/Badge';
import { Card, CardBody } from '@/components/ui/Card';
import { ErrorState } from '@/components/ui/ErrorState';
import { DataView } from '@/components/data/DataView';
import { KPICard } from '@/components/charts/KPICard';
import { useRfm, type RfmRow, type RfmSegment } from '@/api/bi';
import { rangeISO, useFilters } from '@/state/filters';
import { fmt } from '@/lib/format';
import { cn } from '@/lib/cn';

type Tone = 'success' | 'info' | 'warning' | 'danger' | 'neutral';

const SEG_TONE: Record<RfmSegment, Tone> = {
  champions: 'success',
  loyal: 'success',
  'big spenders': 'info',
  new: 'info',
  potential: 'info',
  hibernating: 'warning',
  'at risk': 'warning',
  'cant lose': 'danger',
  lost: 'danger',
};

const SEG_COPY: Record<RfmSegment, string> = {
  champions: 'Buy often, spend big, came in recently. Brag about us.',
  loyal: 'Repeat orderers. Keep them happy with perks.',
  'big spenders': 'High-value buyers. Worth a personal nudge.',
  new: 'Just landed. First impressions are everything.',
  potential: 'Decent activity. Push them up the ladder.',
  hibernating: 'Quiet but not gone. A reminder helps.',
  'at risk': 'Used to love us. Win them back fast.',
  'cant lose': 'High value going cold. Personal save campaign.',
  lost: 'Long gone. Cheap reactivation only.',
};

export function RfmPage() {
  const range = useFilters((s) => s.range);
  const storeIds = useFilters((s) => s.storeIds);
  const timeRangeMode = useFilters((s) => s.timeRangeMode);
  const { start: startISO, end: endISO } = useMemo(
    () => rangeISO(range, timeRangeMode),
    [range, timeRangeMode],
  );

  const query = useRfm({ start: startISO, end: endISO, storeIds, limit: 500 });
  const [search, setSearch] = useState('');
  const [activeSeg, setActiveSeg] = useState<RfmSegment | null>(null);

  const data = query.data;
  const totals = data?.totals;
  const segments = data?.segments ?? [];
  const segMax = Math.max(1, ...segments.map((s) => s.customers));

  const filteredRows = useMemo(() => {
    let rows = data?.rows ?? [];
    if (activeSeg) rows = rows.filter((r) => r.segment === activeSeg);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      rows = rows.filter(
        (r) =>
          (r.name ?? '').toLowerCase().includes(q) ||
          (r.phone ?? '').toLowerCase().includes(q),
      );
    }
    return rows;
  }, [data?.rows, activeSeg, search]);

  const columns = useMemo<ColumnDef<RfmRow, unknown>[]>(
    () => [
      {
        id: 'customer',
        header: 'Customer',
        cell: ({ row }) => (
          <div className="min-w-0">
            <div className="truncate text-sm font-medium">{row.original.name ?? `#${row.original.user_id}`}</div>
            <div className="text-[11px] text-muted-foreground truncate">
              {row.original.phone ?? '—'}
            </div>
          </div>
        ),
      },
      {
        id: 'segment',
        header: 'Segment',
        cell: ({ row }) => (
          <Badge tone={SEG_TONE[row.original.segment]} className="capitalize">
            {row.original.segment}
          </Badge>
        ),
      },
      {
        id: 'rfm',
        header: 'R · F · M',
        cell: ({ row }) => (
          <div className="flex items-center gap-1 font-mono text-[11px] text-muted-foreground">
            <ScorePip n={row.original.r_score} />
            <ScorePip n={row.original.f_score} />
            <ScorePip n={row.original.m_score} />
          </div>
        ),
      },
      {
        id: 'recency',
        header: () => <div className="text-right">Last seen</div>,
        cell: ({ row }) => (
          <div className="text-right text-xs text-muted-foreground">
            {row.original.recency_days}d
          </div>
        ),
      },
      {
        id: 'frequency',
        header: () => <div className="text-right">Orders</div>,
        cell: ({ row }) => (
          <div className="kpi-num text-right text-sm">{fmt.number(row.original.frequency)}</div>
        ),
      },
      {
        id: 'monetary',
        header: () => <div className="text-right">Spend</div>,
        cell: ({ row }) => (
          <div className="kpi-num text-right text-sm font-medium">{fmt.npr(row.original.monetary)}</div>
        ),
      },
    ],
    [],
  );

  return (
    <>
      <PageHeader
        eyebrow="Growth"
        title="RFM segments"
        description="Recency, Frequency, Monetary — bucketed into nine segments so you know who to court, comfort or coax back."
        actions={
          <Badge tone="info" className="gap-1">
            <Sparkles className="h-3 w-3" /> Stage 6
          </Badge>
        }
      />

      <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KPICard label="Customers" value={fmt.number(totals?.customers ?? 0)} loading={query.isLoading} />
        <KPICard label="Orders (window)" value={fmt.number(totals?.orders ?? 0)} loading={query.isLoading} />
        <KPICard label="Revenue" value={fmt.npr(totals?.revenue ?? 0)} loading={query.isLoading} />
        <KPICard
          label="Avg orders / customer"
          value={(totals?.avg_orders ?? 0).toFixed(2)}
          loading={query.isLoading}
          hint={`Avg AOV: ${fmt.npr(totals?.avg_aov ?? 0)}`}
        />
      </div>

      <Card className="mt-4">
        <CardBody>
          <div className="mb-3 flex items-center justify-between">
            <div>
              <h3 className="font-display text-base font-semibold">Segment mix</h3>
              <p className="text-xs text-muted-foreground">
                Tap any segment to filter the table below.
              </p>
            </div>
            {activeSeg ? (
              <button
                type="button"
                onClick={() => setActiveSeg(null)}
                className="press text-[11px] font-medium text-primary hover:underline"
              >
                Clear filter
              </button>
            ) : null}
          </div>

          {query.isLoading ? (
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="h-16 animate-pulse rounded-md bg-muted/40" />
              ))}
            </div>
          ) : segments.length === 0 ? (
            <p className="text-sm text-muted-foreground">No segments to show in this window.</p>
          ) : (
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {segments.map((s) => {
                const tone = SEG_TONE[s.segment];
                const isActive = activeSeg === s.segment;
                const widthPct = Math.max(6, Math.round((s.customers / segMax) * 100));
                return (
                  <button
                    key={s.segment}
                    type="button"
                    onClick={() => setActiveSeg((cur) => (cur === s.segment ? null : s.segment))}
                    className={cn(
                      'press relative overflow-hidden rounded-lg border p-3 text-left focus-ring transition-colors',
                      isActive
                        ? 'border-primary/40 bg-primary/5'
                        : 'border-border bg-surface hover:border-border-strong',
                    )}
                  >
                    <div className="flex items-baseline justify-between gap-2">
                      <Badge tone={tone} className="capitalize">
                        {s.segment}
                      </Badge>
                      <span className="kpi-num text-sm font-semibold">{fmt.number(s.customers)}</span>
                    </div>
                    <div className="mt-2 text-[11px] text-muted-foreground line-clamp-2">
                      {SEG_COPY[s.segment]}
                    </div>
                    <div className="mt-2 flex items-center justify-between text-[11px] text-muted-foreground">
                      <span className="kpi-num">{fmt.npr(s.revenue)}</span>
                      <span>
                        <Users className="inline-block h-3 w-3 align-text-bottom" />{' '}
                        {fmt.number(s.customers)}
                      </span>
                    </div>
                    <div className="mt-2 h-1 w-full overflow-hidden rounded-full bg-muted/60">
                      <div
                        className={cn(
                          'h-full rounded-full transition-[width] duration-300',
                          tone === 'success' && 'bg-success',
                          tone === 'info' && 'bg-info',
                          tone === 'warning' && 'bg-warning',
                          tone === 'danger' && 'bg-danger',
                          tone === 'neutral' && 'bg-muted-foreground/60',
                        )}
                        style={{ width: `${widthPct}%` }}
                      />
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </CardBody>
      </Card>

      <div className="mt-4">
        {query.isError ? (
          <ErrorState description="Couldn't load RFM data." onRetry={() => void query.refetch()} />
        ) : (
          <DataView<RfmRow>
            title={activeSeg ? `Customers in ${activeSeg}` : 'Top customers (by monetary)'}
            data={filteredRows}
            columns={columns}
            isLoading={query.isLoading}
            search={{ value: search, onChange: setSearch, placeholder: 'Search name or phone…' }}
            emptyTitle={activeSeg ? `No "${activeSeg}" customers` : 'No customers in this window'}
          />
        )}
      </div>
    </>
  );
}

function ScorePip({ n }: { n: number }) {
  const tone =
    n >= 4 ? 'bg-success/15 text-success' : n >= 3 ? 'bg-info/15 text-info' : 'bg-muted text-muted-foreground';
  return (
    <span className={cn('inline-flex h-5 w-5 items-center justify-center rounded text-[11px] font-semibold', tone)}>
      {n}
    </span>
  );
}
